import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Stepper,
  Step,
  StepLabel,
  Typography,
  Box,
  Paper,
  Button,
  Snackbar,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import CloseIcon from "@material-ui/icons/Close";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import { useHistory, useLocation } from "react-router-dom";
import useDebouncedSessionStorage from "./useDebouncedSessionStorage";

import ProductDetails from "./steps/ProductDetails";
import StatusSelection from "./steps/StatusSelection";
import DocumentDeliverables from "./steps/DocumentDeliverables";
import AddCampaignID from "./steps/AddCampaignID";
import ReviewAndAddProject from "./steps/ReviewAndAddProject";

import {
  fetchFGStatusesAPI,
  saveStatusSelectionAPI,
  saveCampaignIDAPI,
  submitProductDevelopmentAPI,
  fetchProjectDetailsAPI,
  fetchCampaignMgmtTabsAPI,
  fetchCampaignMgmtColumnsAPI,
  fetchPDStatusesAPI,
} from "./stepperAPI";
import { useStyles } from './ProductDevelopmentStepperStyles';

const steps = [
  "Project Details",
  "Status Selection",
  "Document Deliverables",
  "Add Campaign ID",
  "Review And Add Project",
];

const STORAGE_KEYS = {
  FORM_DATA: "productDevelopmentFormData",
  CURRENT_STEP: "productDevelopmentCurrentStep",
  COMPLETED_STEPS: "productDevelopmentCompletedSteps",
};

const ProductDevelopmentStepper = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const isMountedRef = useRef(true);

  const isEditMode = location?.state?.editProject || false;
  const projectData = location?.state?.projectData || null;
  const isEditCampaign = location?.state?.editCampaign || false;

  const [formData, setFormData] = useState(() => {
    try {
      // For edit mode, do NOT hydrate from sessionStorage – always start from API
      if (isEditMode || isEditCampaign) {
        return {};
      }
      // For new projects, load from sessionStorage if available
      const saved = sessionStorage.getItem(STORAGE_KEYS.FORM_DATA);
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.error("Error reading from sessionStorage:", error);
      return {};
    }
  });
  
  // Starts true in edit mode. The fetch effect below only runs after the first
  // paint, so a default of false let the form render one frame of "NA"
  // placeholders before the data landed and re-rendered the whole page. The
  // loader gate further down keys off this.
  const [loadingProjectData, setLoadingProjectData] = useState(
    () => Boolean(isEditMode || isEditCampaign)
  );

  const [activeStep, setActiveStep] = useState(() => {
    try {
      if (isEditMode || isEditCampaign) {
        return 4;
      }
      // Check if there's already a saved step in sessionStorage
      const saved = sessionStorage.getItem(STORAGE_KEYS.CURRENT_STEP);
      if (saved !== null) {
        const stepIndex = parseInt(saved, 10);
        if (!isNaN(stepIndex) && stepIndex >= 0 && stepIndex < steps.length) {
          return stepIndex;
        }
      }
      return 0;
    } catch {
      return (isEditMode || isEditCampaign) ? 4 : 0;
    }
  });

  const [isInitialized, setIsInitialized] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [skipReplicateDialog, setSkipReplicateDialog] = useState(false);
  const [cancelWarningDialogOpen, setCancelWarningDialogOpen] = useState(false);

  const { debouncedSetItem, flush } = useDebouncedSessionStorage(
    STORAGE_KEYS.FORM_DATA,
    500
  );

  useEffect(() => {
    isMountedRef.current = true;

    // In edit mode, clear any existing session cache so only API data is shown
    if (isEditMode || isEditCampaign) {
      try {
        sessionStorage.removeItem(STORAGE_KEYS.FORM_DATA);
        sessionStorage.removeItem(STORAGE_KEYS.CURRENT_STEP);
        sessionStorage.removeItem(STORAGE_KEYS.COMPLETED_STEPS);
      } catch (e) {
        console.warn("Unable to clear product development session storage in edit mode:", e);
      }
    }

    setIsInitialized(true);
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Fetch project details when in edit mode - call API when Edit Project is clicked
  useEffect(() => {
    const fetchProjectDetails = async () => {
      // Get collabrixProjectID from location state or projectData
      // The project object from dashboard might have different field names
      const collabrixProjectID = projectData?.collabrixProjectID || 
                                 projectData?.collabrixProjectID ||
                                 location?.state?.collabrixProjectID ||
                                 projectData?.id; // Fallback to id if collabrixProjectID not available
      
      if ((isEditMode || isEditCampaign)) {
        if (!collabrixProjectID) {
          console.error("❌ collabrixProjectID is required for edit mode. projectData:", projectData);
          setLoadingProjectData(false);
          return;
        }
        
        try {
          setLoadingProjectData(true);
          console.log("📡 Fetching project details for edit mode - calling API:", collabrixProjectID);
          console.log("📡 projectData received:", projectData);
          // The dashboard's Edit Project action already fetched this exact
          // response (so its own menu control could hold the loader) and handed
          // it over in navigation state — reuse it rather than asking for the
          // same thing twice. Falls back to fetching when opened any other way
          // (Edit Campaign, a direct link, a refresh).
          const prefetched = location?.state?.prefetchedProjectDetails;
          const response =
            prefetched || (await fetchProjectDetailsAPI(collabrixProjectID));
          
          console.log("📡 API Response:", response);
          
          if (response?.status && response?.data) {
            const apiData = response.data;
            const projectDetails = apiData.projectDetails || {};

            console.log("📡 Mapped projectDetails:", projectDetails);
            console.log("📡 API Data structure:", apiData);

            // Edit mode also pulls the Tab/Column configuration and PD statuses
            // from their own endpoints (best-effort — on failure we fall back to
            // whatever fetchProjectDetails embedded).
            //   GET /fetchCampaignMgmtTabs/true
            //   GET /fetchCampaignMgmtColumns/true
            //   GET /fetchPDStatuses/false   (same source as the Sponsor Network tab)
            const [mgmtTabsRes, mgmtColumnsRes, pdStatusesRes] = await Promise.all([
              fetchCampaignMgmtTabsAPI("false").catch((e) => { console.error("fetchCampaignMgmtTabs failed:", e); return null; }),
              fetchCampaignMgmtColumnsAPI("true").catch((e) => { console.error("fetchCampaignMgmtColumns failed:", e); return null; }),
              fetchPDStatusesAPI("false").catch((e) => { console.error("fetchPDStatuses failed:", e); return null; }),
            ]);

            // Pull the first array found under any candidate key. Exact response
            // shape TBD — extra keys are harmless and easy to refine later.
            const pickList = (res, keys) => {
              for (const k of keys) {
                const v = res?.[k] ?? res?.data?.[k] ?? res?.data?.data?.[k];
                if (Array.isArray(v)) return v;
              }
              return [];
            };

            const mgmtTabs = pickList(mgmtTabsRes, [
              "campaignTabConfiguration", "tabConfigurationList", "campaignMgmtTabs", "tabs", "data",
            ]);
            const mgmtColumns = pickList(mgmtColumnsRes, [
              "campaignColumnConfiguration", "columnConfigurationList", "campaignMgmtColumns", "columns", "data",
            ]);

            // PD statuses object (all 6 types); same key set as the Sponsor
            // Network Status Management tab.
            const pdStatusData = pdStatusesRes?.data?.data || pdStatusesRes?.data || {};
            const statusSrc = {
              projectStatuses: pdStatusData.projectStatuses || apiData.projectStatuses || [],
              campaignStatuses: pdStatusData.campaignStatuses || apiData.campaignStatuses || [],
              changeControlStatuses: pdStatusData.changeControlStatuses || apiData.changeControlStatuses || [],
              deviationStatuses: pdStatusData.deviationStatuses || apiData.deviationStatuses || [],
              inProcessTestStatuses: pdStatusData.inProcessTestStatuses || pdStatusData.inProcessStatuses || apiData.inProcessStatuses || [],
              productReleaseTestStatuses: pdStatusData.productReleaseTestStatuses || pdStatusData.projectReleaseStatuses || apiData.projectReleaseStatuses || [],
            };

            // The master list above supplies the status VALUES (which statuses
            // exist). The project's own saved rows supply the SELECTION for each
            // one — the FSD (section 5) is explicit that these toggles "govern
            // which of those values are active for the current project, not the
            // values themselves". Because statusSrc prefers pdStatusData, the
            // saved rows were being read for the list but never for the toggles,
            // so a project's saved selections were replaced by the master's.
            // Same seed-then-override shape the tab/column config uses below.
            //
            // Resolution mirrors Clinical Supply exactly
            // (pharmaCo/ConfigProductDetails/ProductDevelopment/Stepper/steps/
            // StatusSelection.jsx): load the full master list, then overlay the
            // saved set on top of it —
            //
            //   updatedData.poStatuses = prevData.poStatuses.map(status => {
            //     const isActivated = activatedStatuses.poStatuses.some(
            //       s => s.status_id === status.status_id && s.activated === true);
            //     return { ...status, value: isActivated, activated: isActivated };
            //   });
            //
            // The part that matters: a status NOT present in the saved set comes
            // out OFF. Once the project has saved anything for a type, that
            // saved set is the whole truth and the master's own flag is
            // discarded — otherwise a status the user switched off is read back
            // from the master, which defaults it on, and it appears to have
            // sprung back.
            //
            // The master flag is used only when the project has saved NOTHING
            // for that type — Clinical Supply's equivalent of the
            // `Object.keys(activatedStatuses).length === 0 -> seed only` guard.
            const savedStatusRows = {
              projectStatuses: apiData.projectStatuses || [],
              campaignStatuses: apiData.campaignStatuses || [],
              changeControlStatuses: apiData.changeControlStatuses || [],
              deviationStatuses: apiData.deviationStatuses || [],
              inProcessTestStatuses: apiData.inProcessStatuses || [],
              productReleaseTestStatuses: apiData.projectReleaseStatuses || [],
            };
            // BMS and CMO are kept SEPARATE. FSD §5 gives Change Control,
            // Deviation, In-Process Test and Product Release Test independent
            // BMS Selection and CMO Selection toggles (Project and Campaign get
            // a single one), and sponsorSelection / cmoSelection are exactly
            // those two. Collapsing them into one `activated` lost the
            // distinction, so a status set for CMO only came back showing both.
            // `activated` is still emitted for the two single-toggle types.
            // The two sources carry DIFFERENT shapes and both have to be read
            // correctly:
            //   Sponsor Network master (fetchPDStatuses) has ONE flag,
            //     `activated` — there is no BMS/CMO split at master level.
            //   Project rows (fetchProjectDetails) have the pair,
            //     sponsorSelection (BMS) and cmoSelection (CMO).
            // So a master row seeds BOTH sides from its single flag, and only a
            // project row can set them independently. Reading sponsorSelection
            // off a master row yields undefined, which would render every
            // seeded default as off.
            const flagsOf = (r) => {
              if (!r) return { bms: false, cmo: false };
              if (r.sponsorSelection !== undefined || r.cmoSelection !== undefined) {
                return {
                  bms: r.sponsorSelection === true,
                  cmo: r.cmoSelection === true,
                };
              }
              const on = r.activated === true;
              return { bms: on, cmo: on };
            };

            const mapStatuses = (typeKey) => {
              const master = statusSrc[typeKey] || [];
              const saved = savedStatusRows[typeKey] || [];
              const hasSaved = saved.length > 0;
              return master.map((s) => {
                // Saved set wins once it exists; a status missing from it is
                // off. Only with nothing saved for this type does the master's
                // own flag apply.
                const src = hasSaved
                  ? saved.find((r) => r?.statusID === s.statusID)
                  : s;
                const { bms, cmo } = flagsOf(src);
                return {
                  status_id: s.statusID,
                  status_name: s.statusName,
                  activated: bms || cmo,
                  activated_bms: bms,
                  activated_cmo: cmo,
                };
              });
            };

            // fetchCampaignMgmtTabs returns tabs as { campaignTabName, campaignTabType,
            // campaignTabID, activated } with NO overall/system split (each tab can
            // appear twice). Normalize to the { viewTab, tabName, isActive, ... } shape
            // the campaign form expects: dedupe by tab id, then emit one overallView +
            // one systemView entry per tab so both view tables populate. Falls back to
            // the config embedded in fetchProjectDetails.
            // On/off resolves in three tiers: the campaign's saved config wins
            // where it has a row for the tab, otherwise the seed's OWN activated
            // flag is the default, and only then false.
            //
            // This lookup is deliberately only consulted for tabs the campaign
            // actually saved. A tab saved as OFF reads back false and stays OFF;
            // a tab the campaign never saved reads back undefined and falls
            // through to the seed default below.
            // Only systemView rows are authoritative. campaignConfigPayload.js
            // stops writing viewTab:"overallView" rows, so any that already
            // exist in Corda are frozen at their last-written value — OR-ing
            // them in would let a stale ON out-vote a systemView row the user
            // has since turned OFF, and the toggle would spring back on reopen.
            //
            // A campaign saved before that switch may have overallView rows and
            // no systemView rows at all; for those we still read whatever views
            // are present rather than dropping the saved selection entirely.
            const savedTabRows = apiData.campaignTabConfiguration || [];
            const isSystemViewRow = (t) => {
              const v = (t?.viewTab || "").toLowerCase();
              return v === "system" || v === "systemview";
            };
            const systemViewTabRows = savedTabRows.filter(isSystemViewRow);
            const authoritativeTabRows =
              systemViewTabRows.length > 0 ? systemViewTabRows : savedTabRows;

            const campaignTabActiveByName = {};
            // The saved row itself is kept too, not just its on/off flag: the
            // seed template has no knowledge of a tab having been RENAMED or
            // DELETED on this campaign, so building the row purely from the seed
            // dropped both. markForDelete reverted to false and the delete was
            // undone on the next save; newTabName reverted to null and the
            // rename was lost.
            const campaignTabSavedByName = {};
            authoritativeTabRows.forEach((t) => {
              if (!t?.tabName) return;
              campaignTabActiveByName[t.tabName] =
                (campaignTabActiveByName[t.tabName] || false) || !!t.isActive;
              const prev = campaignTabSavedByName[t.tabName];
              campaignTabSavedByName[t.tabName] = {
                // A rename or a delete on any row for this tab name counts.
                newTabName: prev?.newTabName ?? t.newTabName ?? null,
                markForDelete: !!prev?.markForDelete || t.markForDelete === true,
              };
            });

            const buildCampaignTabConfiguration = () => {
              if (mgmtTabs.length > 0) {
                const seen = new Map();
                mgmtTabs.forEach((t) => {
                  const id = t.campaignTabID ?? t.campaignTabName;
                  if (id != null && !seen.has(id)) {
                    const name = t.campaignTabName || t.tabName || "";
                    seen.set(id, {
                      tabName: name,
                      // Saved campaign config wins; otherwise fall back to the
                      // seed's own activated flag — that flag IS the default for
                      // a project that has never saved a tab selection, so
                      // discarding it showed every tab off on a fresh project.
                      isActive:
                        campaignTabActiveByName[name] ??
                        t.activated ??
                        t.isActive ??
                        false,
                      collabrixTabConfigID: t.campaignTabID ?? t.collabrixTabConfigID ?? null,
                      // From the saved row, not the seed — see the note above.
                      newTabName:
                        campaignTabSavedByName[name]?.newTabName &&
                        campaignTabSavedByName[name].newTabName !== "NA"
                          ? campaignTabSavedByName[name].newTabName
                          : null,
                      markForDelete:
                        campaignTabSavedByName[name]?.markForDelete === true,
                    });
                  }
                });
                const rows = [];
                seen.forEach((tab) => {
                  rows.push({ ...tab, viewTab: "overallView" });
                  rows.push({ ...tab, viewTab: "systemView" });
                });
                return rows;
              }
              return (apiData.campaignTabConfiguration || []).map((tab) => ({
                viewTab: tab.viewTab,
                tabName: tab.tabName,
                newTabName: tab.newTabName,
                isActive: tab.isActive,
                markForDelete: tab.markForDelete,
                collabrixTabConfigID: tab.collabrixTabConfigID,
              }));
            };
            const campaignTabConfigurationMapped = buildCampaignTabConfiguration();

            // Columns: the SEED (fetchCampaignMgmtColumns) is the base — it
            // defines the full column list, the correct column->tab linkage
            // (campaignTabID), AND the default on/off via its own activated
            // flag. The campaign's saved campaignColumnConfiguration OVERRIDES
            // that on/off wherever it has a row for the column.
            //
            // Seed and campaign share the same column order (the campaign config
            // was created from this seed), so on/off is matched by index. This
            // also repairs any corrupted tab linkage in the saved campaign config
            // (e.g. every column stored under one tab id) because the linkage is
            // taken from the seed, not the campaign rows.
            const campaignSavedCols = apiData.campaignColumnConfiguration || [];
            const columnsSource =
              mgmtColumns.length > 0
                ? mgmtColumns.map((col, i) => {
                    const saved = campaignSavedCols[i];
                    const on = saved
                      ? !!(saved.isSystemViewSelected ?? saved.isOverallViewSelected ?? saved.activated)
                      // No saved row for this column (a project that saved
                      // nothing, or a seed that has grown since the last save):
                      // the seed's own activated flag is the default.
                      : !!(col.activated ?? col.campaignColumnActivated ?? col.isActive ?? false);
                    return {
                      columnName: col.columnName ?? col.campaignColumnName ?? "",
                      // Rename and delete live on the SAVED row — the seed
                      // template knows nothing about either. Reading them off
                      // the seed reset markForDelete to false (undoing the
                      // delete on the next save) and dropped the rename.
                      newColumnName: saved?.newColumnName ?? col.newColumnName ?? null,
                      markForDelete: saved?.markForDelete === true,
                      // Correct tab linkage comes from the SEED.
                      campaignTabID: col.campaignTabID ?? col.collabrixTabConfigID ?? null,
                      columnType: col.columnType ?? col.campaignColumnType ?? "Default",
                      // Keep the campaign row's column id so a re-save updates
                      // (not duplicates) the existing record.
                      collabrixColumnConfigID:
                        saved?.collabrixColumnConfigID ?? col.collabrixColumnConfigID ?? col.campaignColumnID ?? null,
                      activated: on,
                    };
                  })
                : campaignSavedCols;
            
            // Helpers to build display strings, skipping null/empty parts.
            const campaignDetails = apiData.campaignDetails || {};
            const joinParts = (parts, sep) =>
              parts
                .filter((v) => v !== null && v !== undefined && String(v).trim() !== "")
                .map((v) => String(v).trim())
                .join(sep);

            // fetchProjectDetails returns null for a lot of the project-level
            // fields (productCode, productDescription, projectName, dates, ...)
            // even though the dashboard row that launched this screen already
            // has them. `dashRow` is that row - it is the fallback for every
            // field the edit response leaves empty, so the form shows the same
            // values the user just saw on the dashboard instead of blanks.
            const dashRow = projectData || {};
            const dashCampaign = dashRow.projectDetails?.campaigns?.[0] || {};

            // Prefer the edit response, fall back to the dashboard row.
            const productCode = projectDetails.productCode || dashRow.productCode || "";
            const productDescription =
              projectDetails.productDescription || dashRow.productDescription || "";
            const cmoProductCode =
              projectDetails.cmoProductCode || dashRow.cmoProductCode || "";
            const cmoProjectID = projectDetails.cmoProjectID || dashRow.cmoProjectID || "";
            const sponsorProjectID =
              projectDetails.sponsorProjectID || dashRow.sponsorProjectID || "";

            // Product Code / Description column value (e.g. "2011001 / BMT-996700-01").
            const productLabel = joinParts([productCode, productDescription], " / ");
            // Project ID column value when projectName is absent. Last part is the
            // CMO PRODUCT code, not the CMO project id — matches the dashboard's
            // projectDisplayId (see dashboardAPI).
            const derivedProjectID = joinParts([productCode, sponsorProjectID, cmoProductCode], "-");

            // Resolve collabrixCampaignID robustly. The update payload keys corda's
            // update-vs-create on it, so it must never be null for an existing
            // campaign. Priority: the id explicitly passed from the dashboard row
            // (independent of the edit-response shape) -> a recursive search of the
            // whole API response (handles any nesting: campaignDetails,
            // campaignsList, data.data, etc.) -> projectData fallbacks.
            const deepFindCampaignId = (node, depth = 0) => {
              if (!node || typeof node !== "object" || depth > 6) return null;
              if (node.collabrixCampaignID != null) return node.collabrixCampaignID;
              for (const key of Object.keys(node)) {
                const found = deepFindCampaignId(node[key], depth + 1);
                if (found != null) return found;
              }
              return null;
            };
            const resolvedCampaignID =
              location?.state?.collabrixCampaignID ??
              deepFindCampaignId(apiData) ??
              projectData?.collabrixCampaignID ??
              projectData?.projectDetails?.campaigns?.[0]?.collabrixCampaignID ??
              null;
            console.log("🧩 Resolved collabrixCampaignID:", resolvedCampaignID, {
              fromNavState: location?.state?.collabrixCampaignID,
              fromApiDeepSearch: deepFindCampaignId(apiData),
            });

            // CMO - Site display name from the API's own CDMO enrichment.
            // fetchProjectDetails resolves each cdmoOrgID to cmoOrgName (its
            // Step 6) and uses the literal "Unknown Organization" when it can't,
            // so that placeholder is treated as absent rather than displayed.
            const firstCdmoOrg = projectDetails.cdmoOrgList?.[0];
            const resolvedCdmoOrgName =
              firstCdmoOrg?.cmoOrgName &&
              firstCdmoOrg.cmoOrgName !== "Unknown Organization"
                ? firstCdmoOrg.cmoOrgName
                : "";

            // Map API response to formData structure
            const mappedData = {
              // Also expose at the top level so the save-side fallback
              // (latestData?.collabrixCampaignID) always finds it.
              collabrixCampaignID: resolvedCampaignID,
              productDetails: {
                collabrixProductID: projectDetails.collabrixProductID || "",
                collabrixProjectID: projectDetails.collabrixProjectID || collabrixProjectID,
                // Product Code / Description (read-only). Fall back to the
                // dashboard row's own combined value, then the product id.
                selectedProduct:
                  productLabel ||
                  joinParts([dashRow.productCode, dashRow.productDescription], " / ") ||
                  (projectDetails.collabrixProductID ? String(projectDetails.collabrixProductID) : ""),
                productCode,
                productDescription,
                cmoProductCode,
                cmoProjectID,
                sponsorProjectID,
                // CMO - Site: campaign-level site name (e.g. "PatheonTRO"), then
                // the dashboard row's site NAME, then the CDMO's resolved org
                // NAME from the API, and only then the numeric cdmoOrg id as a
                // last resort (it is an id, not a display name). Without the
                // org-name step a project with no campaign yet showed the raw
                // id (e.g. "5001") in a field the FSD defines as a display name.
                cmoSite:
                  campaignDetails.cmoSite ||
                  dashRow.cmoSite ||
                  resolvedCdmoOrgName ||
                  projectDetails.cdmoOrgList?.[0]?.cdmoOrgID?.toString() ||
                  "",
                // Project ID: match the main dashboard's Project ID column exactly.
                // Dashboard uses productCode-sponsorProjectID-cmoProductCode (see
                // dashboardAPI.projectDisplayId), so derive the same value here first,
                // then fall back to the dashboard's rendered value, and only if
                // neither can be built fall back to projectName / raw projectId.
                projectID:
                  derivedProjectID ||
                  dashRow.projectDisplayId ||
                  projectDetails.projectName ||
                  dashRow.projectId ||
                  "",
                // Product attributes shown read-only (null from API -> "NA").
                productModality: projectDetails.drugTypeName || "NA",
                productTypeName: projectDetails.productTypeName || "NA",
                manufacturingFacilityType: projectDetails.facilityTypeName || "NA",
                manufacturingSupplyType: projectDetails.supplyTypeName || "NA",
                // deliverableTypes is [] on the edit response; the dashboard row
                // carries the names as a comma-separated string, so split it back
                // into the array the form expects.
                deliverableType: (projectDetails.deliverableTypes || []).length > 0
                  ? projectDetails.deliverableTypes.map(dt => dt.deliverableTypeName || dt.deliverableTypeID)
                  : (typeof dashRow.deliverableType === "string" && dashRow.deliverableType.trim() !== ""
                      ? dashRow.deliverableType.split(",").map(s => s.trim()).filter(Boolean)
                      : []),
                deliverableTypeIDs: (projectDetails.deliverableTypes || []).map(dt => dt.deliverableTypeID).join(","),
                projectStartDate: projectDetails.projectStartDate || null,
                projectEndDate: projectDetails.projectEndDate || null,
                sponsorOrgID: projectDetails.sponsorOrgID || "",
                cdmoOrgID:
                  projectDetails.cdmoOrgList?.[0]?.cdmoOrgID ||
                  dashRow.cmoOrgID ||
                  dashRow.vendorMosymphonyID ||
                  "",
              },
              // Status VALUES come from fetchPDStatuses (all 6 types), same as
              // the Sponsor Network Status Management tab, falling back to the
              // config embedded in fetchProjectDetails. The per-status SELECTION
              // comes from the project's own saved rows where it has them — see
              // mapStatuses / savedStatusSelection above.
              fgStatuses: {
                projectStatuses: mapStatuses("projectStatuses"),
                campaignStatuses: mapStatuses("campaignStatuses"),
                changeControlStatuses: mapStatuses("changeControlStatuses"),
                deviationStatuses: mapStatuses("deviationStatuses"),
                inProcessTestStatuses: mapStatuses("inProcessTestStatuses"),
                productReleaseTestStatuses: mapStatuses("productReleaseTestStatuses"),
              },
              // Document Deliverables - API returns fileFormatID, map to fileFormatTypeID for UI
              documentDeliverables: (apiData.documentDetailsList || []).map(doc => ({
                documentTypeID: doc.documentTypeID || 0,
                fileFormatTypeID: doc.fileFormatID || doc.fileFormatTypeID || 0, // API uses fileFormatID
                orgResponsibleID: doc.uploadOrgCollaboratorTypeID || 0,
                documentCategoryID: doc.documentCategoryID || null,
                documentGroupID: doc.documentGroupID || null,
              })),
              campaignID: {
                // collabrixCampaignID must survive to the update payload. Source
                // it from the API first, then fall back to the row/projectData
                // passed from the dashboard. Use ?? so a numeric id (e.g. 1) is
                // not dropped as falsy.
                collabrixCampaignID: resolvedCampaignID,
                campaignID: campaignDetails.campaignID || dashRow.campaignId || dashCampaign.campaignId || "",
                poNumber: campaignDetails.poNumber || dashRow.poNumber || "",
                // ?? not || - productQuantityRequired is 0 for a fresh campaign
                // and 0 must survive instead of being replaced by the fallback.
                productQuantity:
                  campaignDetails.productQuantityRequired ??
                  dashRow.contractedQty ??
                  "",
                // The API field is lowercase `uom`; the previous `Uom` never
                // matched, so this always fell through to the "KG" default.
                uom: campaignDetails.uom || campaignDetails.Uom || dashRow.uom || "KG",
                defaultView: campaignDetails.viewType || "System",
                campaignStartDate:
                  campaignDetails.campaignStartDate || dashRow.startDate || null,
                campaignEndDate:
                  campaignDetails.campaignEndDate || dashRow.expectedEndDate || null,
                manufacturingFacility: campaignDetails.manufacturingFacility || "",
                // ?? not || - dataGranularityID 0 is a real value.
                dataGranularityID: campaignDetails.dataGranularityID ?? null,
                // ?? so numberOfStages 0 renders as "0" rather than "".
                numberOfStages:
                  campaignDetails.numberOfStages != null
                    ? campaignDetails.numberOfStages.toString()
                    : "",
                campaignStatusID: campaignDetails.campaignStatusID ?? null,
                campaignStatusText:
                  campaignDetails.campaignStatusText || dashRow.campaignStatus || null,
                campaignStageDetails: (apiData.campaignStageDetails || []).map(stage => ({
                  stageNumber: stage.stageNumber,
                  sponsorStageID: stage.sponsorStageID,
                  cmoStageID: stage.cmoStageID,
                  isActive: stage.isActive,
                  collabrixCampaignStageID: stage.collabrixCampaignStageID,
                })),
                campaignBOMDetails: (apiData.campaignBOMDetails || []).map(bom => ({
                  cmoMaterialCode: bom.cmoMaterialCode,
                  cmoMaterialDescription: bom.cmoMaterialDescription,
                  cmoStageIDs: bom.cmoStageIDs,
                  cmoUOM: bom.cmoUOM,
                  supplierName: bom.supplierName,
                  countryOfOrigin: bom.countryOfOrigin,
                  isActive: bom.isActive,
                  sponsorMaterialCode: bom.sponsorMaterialCode,
                  sponsorMaterialDescription: bom.sponsorMaterialDescription,
                  sponsorUOM: bom.sponsorUOM,
                  materialProvidedBySponsor: bom.materialProvidedBySponsor,
                  collabrixBomID: bom.collabrixBomID,
                })),
                // Tab config from fetchCampaignMgmtTabs (normalized above),
                // falling back to fetchProjectDetails' embedded config.
                campaignTabConfiguration: campaignTabConfigurationMapped,
                // Column config from fetchCampaignMgmtColumns, else embedded config.
                // Tolerant of both the new (campaign*) and legacy field names until
                // the column response shape is confirmed.
                campaignColumnConfiguration: columnsSource.map(col => ({
                  columnName: col.columnName ?? col.campaignColumnName ?? "",
                  newColumnName: col.newColumnName ?? null,
                  isOverallViewSelected: col.isOverallViewSelected ?? col.overallView ?? col.activated ?? false,
                  isSystemViewSelected: col.isSystemViewSelected ?? col.systemView ?? col.activated ?? false,
                  columnType: col.columnType ?? col.campaignColumnType ?? "Default",
                  markForDelete: col.markForDelete ?? false,
                  collabrixTabConfigID: col.collabrixTabConfigID ?? col.campaignTabID ?? null,
                  collabrixColumnConfigID: col.collabrixColumnConfigID ?? col.campaignColumnID ?? null,
                })),
              },
            };
            
            // Store in sessionStorage
            sessionStorage.setItem(STORAGE_KEYS.FORM_DATA, JSON.stringify(mappedData));
            setFormData(mappedData);
            console.log("✅ Project details loaded and mapped:", mappedData);
          } else {
            console.warn("⚠️ API response missing data:", response);
          }
        } catch (error) {
          console.error("❌ Error fetching project details:", error);
        } finally {
          setLoadingProjectData(false);
        }
      }
    };
    
    fetchProjectDetails();
  }, [isEditMode, isEditCampaign, projectData?.collabrixProjectID, location?.state?.collabrixProjectID]);

  // For edit mode, always go to Review page (step 4) which shows all sections
  // The ReviewAndAddProject component will display all sections in read-only mode
  // Users can then click "Edit" on individual sections to edit them
  useEffect(() => {
    try {
      // If in edit mode, always ensure we're on the Review page (step 4)
      // This ensures all sections are visible when editing from dashboard
      if (isEditMode || isEditCampaign) {
        // Force set to step 4 and save to sessionStorage
        setActiveStep(4);
        sessionStorage.setItem(STORAGE_KEYS.CURRENT_STEP, "4");
        return;
      }

      // For non-edit mode, check if there's already a saved step in sessionStorage
      const savedStep = sessionStorage.getItem(STORAGE_KEYS.CURRENT_STEP);
      
      // Only proceed with step determination if there's no saved step
      // This prevents overwriting the user's current position on page refresh
      if (savedStep !== null) {
        return; // Exit early if step is already saved
      }

      // For new projects, start at step 0
      // (This logic is only for non-edit mode)
    } catch (error) {
      console.error("Error handling project edit step determination:", error);
    }
  }, [isEditMode, isEditCampaign]);

  // Additional effect to ensure activeStep stays at 4 in edit mode
  useEffect(() => {
    if ((isEditMode || isEditCampaign) && activeStep !== 4) {
      setActiveStep(4);
      sessionStorage.setItem(STORAGE_KEYS.CURRENT_STEP, "4");
    }
  }, [isEditMode, isEditCampaign, activeStep]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      flush();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      flush();
    };
  }, [flush]);

  const cleanStepData = useCallback((stepName, stepData) => {
    if (!stepData || typeof stepData !== "object") {
      return stepData;
    }

    const cleaned = {};

    if (stepName === "productDetails") {
      const validFields = [
        "selectedProduct",
        "projectID",
        "cmoSite",
        "deliverableType",
        "projectStartDate",
        "projectEndDate",
        "productModality",
        "manufacturingFacilityType",
        "productTypeName",
        "manufacturingSupplyType",
        // IDs and metadata needed by later steppers / APIs
        "collabrixProductID",
        "collabrixProjectID",
        "cdmoOrgID",
        "sponsorOrgID",
        "deliverableTypeIDs",
      ];
      validFields.forEach((field) => {
        if (stepData.hasOwnProperty(field)) {
          if (field === "deliverableType") {
            const value = stepData[field];
            cleaned[field] = value 
              ? (Array.isArray(value) ? value : [value])
              : [];
          } else {
            cleaned[field] = stepData[field];
          }
        }
      });
    } else if (stepName === "fgStatuses") {
      // All six PD status types must be listed. The four BMS/CMO ones were
      // missing, so every toggle in Change Control / Deviation / In-Process Test
      // / Product Release Test was silently stripped here on its way to
      // formData: the section's Save button never enabled (hasSectionChanges
      // compares stored data and saw no change) and those four types could
      // never be saved at all.
      // poStatuses / batchStatuses are Commercial & Clinical Supply shapes,
      // kept so this stays usable from those flows.
      const validFields = [
        "projectStatuses",
        "campaignStatuses",
        "changeControlStatuses",
        "deviationStatuses",
        "inProcessTestStatuses",
        "productReleaseTestStatuses",
        "poStatuses",
        "batchStatuses",
      ];
      validFields.forEach((field) => {
        if (stepData.hasOwnProperty(field)) {
          cleaned[field] = stepData[field];
        }
      });
    } else if (stepName === "documentDeliverables") {
      return Array.isArray(stepData) ? stepData : [];
    } else if (stepName === "campaignID") {
      const validFields = [
        "campaignID",
        "poNumber",
        "productQuantity",
        "numberOfStages",
        "uom",
        "defaultView",
        "dataGranularity",
        "campaignStartDate",
        "campaignEndDate",
        "manufacturingFacility",
        "stageMapping",
        "materialsNotProvidedByBMS",
        "materialsProvidedByBMS",
        "tabConfiguration",
        "columnConfigurations",
      ];
      validFields.forEach((field) => {
        if (stepData.hasOwnProperty(field)) {
          cleaned[field] = stepData[field];
        }
      });
      delete cleaned.newTabName;
    } else {
      return stepData;
    }

    return cleaned;
  }, []);

  const updateStepData = useCallback(
    (stepName, stepData) => {
      try {
        if (!isMountedRef.current) return;

        const cleanedStepData = cleanStepData(stepName, stepData);

        setFormData((prevData) => {
          if (prevData[stepName] === cleanedStepData) {
            return prevData;
          }

          if (
            typeof prevData[stepName] === "object" &&
            typeof cleanedStepData === "object" &&
            prevData[stepName] !== null &&
            cleanedStepData !== null
          ) {
            try {
              if (
                JSON.stringify(prevData[stepName]) === JSON.stringify(cleanedStepData)
              ) {
                return prevData;
              }
            } catch (e) {
            }
          }

          const validStepNames = [
            "productDetails",
            "fgStatuses",
            "documentDeliverables",
            "campaignID",
          ];

          if (!validStepNames.includes(stepName)) {
            console.warn(`Invalid step name: ${stepName}. Only valid step names will be stored.`);
            return prevData;
          }

          const updatedData = {
            ...prevData,
            [stepName]: cleanedStepData,
          };

          debouncedSetItem(updatedData);

          return updatedData;
        });
      } catch (error) {
        console.error(`Error updating step data for ${stepName}:`, error);
      }
    },
    [debouncedSetItem, cleanStepData]
  );

  const markStepCompleted = useCallback((stepIndex) => {
    try {
      const completedSteps = JSON.parse(
        sessionStorage.getItem(STORAGE_KEYS.COMPLETED_STEPS) || "[]"
      );
      if (!completedSteps.includes(stepIndex)) {
        completedSteps.push(stepIndex);
        sessionStorage.setItem(
          STORAGE_KEYS.COMPLETED_STEPS,
          JSON.stringify(completedSteps)
        );
      }
    } catch (error) {
      console.error("Error marking step as completed:", error);
    }
  }, []);

  const markApiCallSuccessful = useCallback(
    (shouldNavigate = true, nextStepIndex, stepName = null) => {
      markStepCompleted(activeStep);

      const displayStepName = stepName || steps[activeStep];

      setSnackbar({
        open: true,
        message: `${displayStepName} saved successfully`,
        severity: "success",
      });

      if (shouldNavigate) {
        const nextStep =
          typeof nextStepIndex === "number" ? nextStepIndex : activeStep + 1;
        sessionStorage.setItem(STORAGE_KEYS.CURRENT_STEP, nextStep.toString());
        setActiveStep(nextStep);
      }
    },
    [activeStep, markStepCompleted]
  );

  const handleNext = useCallback(
    (stepIndex) => {
      try {
        let nextStep;
        if (typeof stepIndex === "number") {
          nextStep = stepIndex;
        } else {
          nextStep = activeStep + 1;
        }

        markStepCompleted(activeStep);

        sessionStorage.setItem(STORAGE_KEYS.CURRENT_STEP, nextStep.toString());

        flush();

        if (isMountedRef.current) {
          setActiveStep(nextStep);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } catch (error) {
        console.error("Error in handleNext:", error);
        if (isMountedRef.current) {
          setSnackbar({
            open: true,
            message: "An error occurred while proceeding to next step",
            severity: "error",
          });
        }
      }
    },
    [activeStep, markStepCompleted, flush]
  );

  const handleBack = useCallback(() => {
    try {
      if (activeStep === undefined || activeStep <= 0) {
        throw new Error("Already at first step");
      }

      setActiveStep((prevActiveStep) => {
        const newStep = Math.max(0, prevActiveStep - 1);
        sessionStorage.setItem(STORAGE_KEYS.CURRENT_STEP, newStep.toString());
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return newStep;
      });
    } catch (error) {
      console.error("Error in handleBack:", error);
      setSnackbar({
        open: true,
        message: "Cannot go back from first step",
        severity: "error",
      });
    }
  }, [activeStep]);

  const handleGoToStep = useCallback((stepIndex) => {
    try {
      if (stepIndex < 0 || stepIndex >= steps.length) {
        throw new Error("Invalid step index");
      }
      
      flush();
      
      sessionStorage.setItem(STORAGE_KEYS.CURRENT_STEP, stepIndex.toString());
      
      if (isMountedRef.current) {
        setActiveStep(stepIndex);
      }
    } catch (error) {
      console.error("Error in handleGoToStep:", error);
      setSnackbar({
        open: true,
        message: "An error occurred while navigating to step",
        severity: "error",
      });
    }
  }, [steps.length, flush]);

  const handleSubmit = useCallback(() => {
    try {
      flush();

      markStepCompleted(activeStep);

      setSnackbar({
        open: true,
        message: "Project configuration submitted successfully",
        severity: "success",
      });

      setTimeout(() => {
        sessionStorage.removeItem(STORAGE_KEYS.FORM_DATA);
        sessionStorage.removeItem(STORAGE_KEYS.CURRENT_STEP);
        sessionStorage.removeItem(STORAGE_KEYS.COMPLETED_STEPS);

        history.push(
          `/pcmp/${localStorage.collabType}/${localStorage.roleName}/${localStorage.organizationName}/${localStorage.ecosystemId}/productdevelopment`
        );
      }, 2000);
    } catch (error) {
      console.error("Submission error:", error);
      setSnackbar({
        open: true,
        message: error.message || "An error occurred during submission",
        severity: "error",
      });
    }
  }, [activeStep, history, flush, markStepCompleted]);

  const handleGoBack = useCallback(() => {
    flush();

    sessionStorage.removeItem(STORAGE_KEYS.FORM_DATA);
    sessionStorage.removeItem(STORAGE_KEYS.CURRENT_STEP);
    sessionStorage.removeItem(STORAGE_KEYS.COMPLETED_STEPS);

    history.push(
      `/pcmp/${localStorage.collabType}/${localStorage.roleName}/${localStorage.organizationName}/${localStorage.ecosystemId}/productdevelopment`
    );
  }, [history, flush]);

  const handleCancelClick = useCallback(() => {
    if (activeStep === 2) {
      setCancelWarningDialogOpen(true);
    } else {
      handleGoBack();
    }
  }, [activeStep, handleGoBack]);

  const handleConfirmCancel = useCallback(() => {
    setCancelWarningDialogOpen(false);
    handleGoBack();
  }, [handleGoBack]);

  const handleCloseCancelWarning = useCallback(() => {
    setCancelWarningDialogOpen(false);
  }, []);

  const handleCloseSnackbar = useCallback((event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const getStepContent = useCallback(
    (step) => {
      if (step === undefined || step === null) {
        console.error("Invalid step value");
        return null;
      }

      const commonProps = {
        formData: formData || {},
        updateStepData,
        classes,
        handleNext,
        handleBack,
        handleSubmit,
        handleGoToStep,
        activeStep,
        stepsLength: steps.length,
        markApiCallSuccessful,
        readOnly: false,
        onValidationChange: () => {},
        onValidationError: () => {},
        skipReplicateDialog,
        setSkipReplicateDialog,
        isProductDetailsEditMode: false,
        materialDetails: formData?.materialDetails,
      };

      try {
        switch (step) {
          case 0:
            return <ProductDetails {...commonProps} />;
          case 1:
            return <StatusSelection {...commonProps} fetchFGStatusesAPI={fetchFGStatusesAPI} />;
          case 2:
            return <DocumentDeliverables {...commonProps} fetchFGStatusesAPI={fetchFGStatusesAPI} />;
          case 3:
            return <AddCampaignID {...commonProps} />;
          case 4:
            return <ReviewAndAddProject {...commonProps} updateStepData={updateStepData} markApiCallSuccessful={markApiCallSuccessful} isEditMode={isEditMode} isEditCampaign={isEditCampaign} />;
          default:
            return "Unknown step";
        }
      } catch (error) {
        console.error("Error rendering step content:", error);
        return (
          <div>
            <Typography color="error">
              An error occurred while loading this step. Please try again.
            </Typography>
            <Button
              onClick={() => window.location.reload()}
              variant="outlined"
              color="primary"
            >
              Reload
            </Button>
          </div>
        );
      }
    },
    [
      formData,
      updateStepData,
      classes,
      handleNext,
      handleBack,
      handleSubmit,
      activeStep,
      markApiCallSuccessful,
    ]
  );

  const stepContent = useMemo(() => {
    return getStepContent(activeStep);
  }, [activeStep, getStepContent]);

  // Edit mode waits for the project data too, so the first thing rendered is
  // this loader rather than a form of "NA" placeholders that then swaps out.
  if (!isInitialized || loadingProjectData) {
    return (
      <Box
        p={3}
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className={classes.root} style={{ backgroundColor: "#fff" }}>
      <Paper className={classes.root} elevation={0} style={{ backgroundColor: "#fff", width: "100%" }}>
        <Box className={classes.stepperHeader} style={{ backgroundColor: "#F5F9FF", width: "100%" }}>
          <Typography className={classes.heading} variant="h5">
            {isEditMode ? "Edit Project" : "Add New Project"}
          </Typography>
          <Typography className={classes.subheading}>
            {isEditMode 
              ? "Please review and update the project details and configurations in the fields below."
              : "Please add the project details and configurations in the fields below."}
          </Typography>
          <Box
            className="close-page-action"
            onClick={handleCancelClick}
            style={{
              position: "absolute",
              top: "32px",
              right: "32px",
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              gap: "8px",
            }}
          >
            <Typography>Cancel</Typography>
            <CloseIcon />
          </Box>
        </Box>
        {/* Hide stepper in edit mode - user only sees review page directly */}
        {!isEditMode && (
          <Box className={classes.stepperContainer}>
            <Stepper activeStep={activeStep} className={classes.stepper}>
              {steps.map((label, index) => {
                const stepProps = {};
                const labelProps = {};

                return (
                  <Step key={label} {...stepProps}>
                    <StepLabel
                      style={{ whiteSpace: "nowrap" }}
                      className={classes.stepLabel}
                      {...labelProps}
                      StepIconProps={{
                        completed:
                          index < activeStep && (
                            <CheckCircleIcon className={classes.stepCompleted} />
                          ),
                      }}
                    >
                      {label}
                    </StepLabel>
                  </Step>
                );
              })}
            </Stepper>
          </Box>
        )}
        <Box className={classes.contentWrapper}>
          <Box className={`${classes.stepContent} ${classes.twoColFormsGrid}`}>{stepContent}</Box>
        </Box>
        <Snackbar
          anchorOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          key={snackbar.message}
          style={{ zIndex: 9999 }}
        >
          <MuiAlert
            elevation={6}
            variant="filled"
            severity={snackbar.severity}
            onClose={handleCloseSnackbar}
            action={
              <IconButton
                size="small"
                color="inherit"
                onClick={handleCloseSnackbar}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          >
            {snackbar.message}
          </MuiAlert>
        </Snackbar>

        <Dialog
          open={cancelWarningDialogOpen}
          onClose={handleCloseCancelWarning}
          aria-labelledby="cancel-warning-dialog-title"
          aria-describedby="cancel-warning-dialog-description"
          maxWidth="sm"
          fullWidth
          PaperProps={{
            style: {
              borderRadius: "8px",
              padding: "8px",
            },
          }}
        >
          <DialogTitle 
            id="cancel-warning-dialog-title"
            style={{
              padding: "24px 24px 16px 24px",
              fontSize: "20px",
              fontWeight: 600,
              color: "#d32f2f",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            Confirm Cancellation
          </DialogTitle>
          <DialogContent style={{ padding: "24px" }}>
            <DialogContentText 
              id="cancel-warning-dialog-description"
              style={{
                fontSize: "15px",
                lineHeight: "1.6",
                color: "#424242",
                marginBottom: "8px",
              }}
            >
              Exiting now will permanently delete all unsaved project details and document information.
            </DialogContentText>
            <DialogContentText 
              style={{
                fontSize: "14px",
                lineHeight: "1.5",
                color: "#757575",
                marginTop: "12px",
                fontStyle: "italic",
              }}
            >
              All data must be re-entered if you proceed.
            </DialogContentText>
            <Box
              style={{
                marginTop: "20px",
                padding: "12px",
                backgroundColor: "#fff3cd",
                borderRadius: "4px",
                borderLeft: "4px solid #ffc107",
              }}
            >
              <Typography
                style={{
                  fontSize: "13px",
                  color: "#856404",
                  fontWeight: 500,
                }}
              >
                ⚠️ This action cannot be undone.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions
            style={{
              padding: "16px 24px",
              borderTop: "1px solid #e0e0e0",
              justifyContent: "flex-end",
              gap: "12px",
            }}
          >
            <Button
              onClick={handleCloseCancelWarning}
              variant="outlined"
              style={{
                textTransform: "none",
                padding: "8px 24px",
                borderRadius: "4px",
                borderColor: "#1976d2",
                color: "#1976d2",
                fontWeight: 500,
                minWidth: "120px",
              }}
            >
              Stay on Page
            </Button>
            <Button
              onClick={handleConfirmCancel}
              variant="contained"
              style={{
                textTransform: "none",
                padding: "8px 24px",
                borderRadius: "4px",
                backgroundColor: "#d32f2f",
                color: "#fff",
                fontWeight: 500,
                minWidth: "120px",
                boxShadow: "0 2px 4px rgba(211, 47, 47, 0.3)",
              }}
            >
              Cancel & Exit
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </div>
  );
};

export default ProductDevelopmentStepper;

